import React, { useEffect, useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import { useDispatch, useSelector } from "react-redux";
import {
  breakpoints,
  FilterCells,
  useMediaQuery,
  SegmentedControl,
  SegmentedControlTab,
  Button,
  useColorTokens
} from "@tvg/design-system";
import {
  RaceWagerType,
  RaceProgram,
  RaceTypeCodeEnum
} from "@tvg/ts-types/Race";
import { setRunnerModifierExpanded } from "../../../store/actions";
import { getIsRunnerModifierExpanded } from "../../../store/selectors";
import {
  Container,
  Divider,
  RaceCardViewButtonContainer,
  RunnerModifierContainer,
  SegmentedControlContainer,
  SelectAllContainer
} from "./styled-components";
import { RunnerModifierEnum } from "../../../types";
import RunnerSort, { RunnerSortProps } from "./RunnerSort";
import SelectAll from "./SelectAll";

interface RunnerModifierProps extends RunnerSortProps {
  value: RunnerModifierEnum;
  getRunnerModifier: (val: RunnerModifierEnum) => void;
  isDesktopPicksFormat?: boolean;
  betType?: RaceWagerType;
  betLeg?: number;
  selections: string[][];
  isScrollableRaceCard?: boolean;
  currentRace?: RaceProgram;
}

const AnimatedSegmentControlContainer = animated(SegmentedControlContainer);

const RunnerModifier = ({
  getRunnerModifier,
  value,
  sortOrder,
  sortBy,
  onSortOrderChange,
  onSortByChange,
  betType,
  betLeg,
  selections,
  isDesktopPicksFormat = false,
  isScrollableRaceCard,
  currentRace
}: RunnerModifierProps) => {
  const dispatch = useDispatch();
  const colorTokens = useColorTokens();
  const isDesktop = useMediaQuery(breakpoints.tablet.min.sm);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const isRunnerModifierExpanded = useSelector(getIsRunnerModifierExpanded);

  const { x } = useSpring({
    from: { x: 0 },
    x: isRunnerModifierExpanded ? 1 : 0,
    config: { duration: 200 },
    immediate: isFirstRender
  });

  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  return (
    <Container>
      {!isDesktop && (
        <AnimatedSegmentControlContainer
          style={{
            height: x.to({
              range: [0, 1],
              output: [0, 56]
            })
          }}
        >
          <SegmentedControl
            selectedValue={value}
            onChange={(event) =>
              getRunnerModifier(event.nativeEvent.value as RunnerModifierEnum)
            }
            size="m"
            qaLabel="runner-modifier-segmented-control"
          >
            <SegmentedControlTab
              value={RunnerModifierEnum.DEFAULT}
              qaLabel="default-tab"
            >
              Default
            </SegmentedControlTab>
            <SegmentedControlTab
              value={RunnerModifierEnum.ADVANCED}
              qaLabel="advanced-tab"
            >
              Advanced
            </SegmentedControlTab>
            <SegmentedControlTab
              value={RunnerModifierEnum.PERFORMANCE}
              qaLabel="performance-tab"
            >
              Performance
            </SegmentedControlTab>
          </SegmentedControl>
        </AnimatedSegmentControlContainer>
      )}
      <RunnerModifierContainer
        data-qa-label="runner-modifier-container"
        isDesktopPicksFormat={isDesktopPicksFormat}
      >
        {isDesktop ? (
          <FilterCells
            size="m"
            bg={colorTokens.background.surface}
            onChange={(val) => getRunnerModifier(val as RunnerModifierEnum)}
            filterOptions={[
              { label: "Default", value: RunnerModifierEnum.DEFAULT },
              { label: "Advanced", value: RunnerModifierEnum.ADVANCED },
              { label: "Performance", value: RunnerModifierEnum.PERFORMANCE }
            ]}
            activeValue={value}
            qaLabel="runner-modifier"
            hasShadow={true}
          />
        ) : (
          <RaceCardViewButtonContainer isActive={isRunnerModifierExpanded}>
            <Button
              size="m"
              variant="tertiary"
              qaLabel="race-card-view-toggle-button"
              onPress={() => {
                dispatch(setRunnerModifierExpanded(!isRunnerModifierExpanded));
              }}
              icon="settings"
              iconPosition="start"
            >
              Racecard View
            </Button>
          </RaceCardViewButtonContainer>
        )}
        {!isDesktop && <Divider />}
        <RunnerSort
          sortOrder={sortOrder}
          onSortOrderChange={onSortOrderChange}
          sortBy={sortBy}
          onSortByChange={onSortByChange}
          isHarnessRace={currentRace?.type?.code === RaceTypeCodeEnum.HARNESS}
        />
        {currentRace?.wagerable && betType && !isDesktopPicksFormat && (
          <SelectAllContainer data-qa-label="select-all-container">
            <SelectAll
              leg={betLeg}
              selectedBetType={betType}
              currentRace={currentRace}
              selections={selections}
              isScrollableRacecard={isScrollableRaceCard}
            />
          </SelectAllContainer>
        )}
      </RunnerModifierContainer>
    </Container>
  );
};
export default RunnerModifier;
